import { FaCalculator, FaDownload, FaPlane, FaPlus, FaSpinner, FaTrash, FaUserFriends } from 'react-icons/fa';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';

const Api11Form = ({ state, updateState }) => {
    const formData = state.formData || {};
    const travelEntries = state.travelEntries || [];
    const commutingData = state.commutingData || {
        headcount: '', daysPerWeek: '', weeksPerYear: '',
        modes: [
            { mode: 'car_solo', percentage: '', distance: '' },
            { mode: 'carpool', percentage: '', distance: '', occupancy: '2.2' },
            { mode: 'bus', percentage: '', distance: '' },
            { mode: 'metro', percentage: '', distance: '' },
            { mode: 'rail', percentage: '', distance: '' },
            { mode: 'motorcycle', percentage: '', distance: '' },
            { mode: 'bike', percentage: '', distance: '' },
            { mode: 'walk', percentage: '', distance: '' },
            { mode: 'shuttle', percentage: '', distance: '' },
            { mode: 'telework', percentage: '', distance: '' }
        ],
        carFuelType: 'unknown'
    };
    const CLIMATIQ_API_KEY = process.env.REACT_APP_CLIMATIQ_API_KEY || 'Y2ZBNWZKRzRGNzJYNjRQMzpa';

    const travelTypes = {
        'flight': {
            label: 'Flight',
            fields: ['origin', 'destination', 'cabinClass', 'trips'],
            cabinClasses: ['economy', 'premium-economy', 'business', 'first'],
            payload: {
                emission_factor: {
                    activity_id: "passenger_flight-route_type_domestic-aircraft_type_na-distance_na-class_na-rf_na-distance_uplift_na",
                    data_version: "^0"
                },
                parameters: {
                    passengers: 4,
                    distance: 100,
                    distance_unit: "km"
                }
            }
        },
        'hotel': {
            label: 'Hotel Stay',
            fields: ['city', 'country', 'roomNights'],
            payload: {
                emission_factor: {
                    activity_id: "hospitality_type_hotel",
                    data_version: "^0"
                },
                parameters: {
                    activity: 1,
                    activity_unit: "room_night"
                }
            }
        },
        'ground': {
            label: 'Ground Transport',
            fields: ['mode', 'distance', 'fuelType'],
            modes: ['taxi', 'rental', 'rail', 'bus'],
            fuelTypes: ['unknown', 'gasoline', 'diesel', 'hybrid', 'ev'],
            payload: {
                emission_factor: {
                    activity_id: "passenger_vehicle-vehicle_type_car-fuel_type_gasoline-vehicle_size_na-engine_size_na",
                    data_version: "^0"
                },
                parameters: {
                    distance: 100,
                    distance_unit: "km"
                }
            }
        }
    };

    const commutingModes = {
        'car_solo': {
            label: 'Car (Solo)',
            unit: 'km',
            hasFuel: true,
            payload: {
                emission_factor: {
                    activity_id: "passenger_vehicle-vehicle_type_car-fuel_type_gasoline-vehicle_size_na-engine_size_na",
                    data_version: "^0"
                },
                parameters: {
                    distance: 100,
                    distance_unit: "km"
                }
            }
        },
        'carpool': {
            label: 'Carpool',
            unit: 'km',
            hasOccupancy: true,
            payload: {
                emission_factor: {
                    activity_id: "passenger_vehicle-vehicle_type_car-fuel_type_gasoline-vehicle_size_na-engine_size_na",
                    data_version: "^0"
                },
                parameters: {
                    distance: 100,
                    distance_unit: "km"
                }
            }
        },
        'bus': {
            label: 'Bus',
            unit: 'km',
            payload: {
                emission_factor: {
                    activity_id: "passenger_vehicle-vehicle_type_bus-fuel_type_na-vehicle_size_na-engine_size_na",
                    data_version: "^0"
                },
                parameters: {
                    distance: 100,
                    distance_unit: "km"
                }
            }
        },
        'metro': {
            label: 'Metro',
            unit: 'km',
            payload: {
                emission_factor: {
                    activity_id: "passenger_train-route_type_urban_rail-fuel_type_na",
                    data_version: "^0"
                },
                parameters: {
                    distance: 100,
                    distance_unit: "km"
                }
            }
        },
        'rail': {
            label: 'Commuter Rail',
            unit: 'km',
            payload: {
                emission_factor: {
                    activity_id: "passenger_train-route_type_regional_rail-fuel_type_na",
                    data_version: "^0"
                },
                parameters: {
                    distance: 100,
                    distance_unit: "km"
                }
            }
        },
        'motorcycle': {
            label: 'Motorcycle',
            unit: 'km',
            hasFuel: true,
            payload: {
                emission_factor: {
                    activity_id: "passenger_vehicle-vehicle_type_motorcycle-fuel_type_na-vehicle_size_na-engine_size_na",
                    data_version: "^0"
                },
                parameters: {
                    distance: 100,
                    distance_unit: "km"
                }
            }
        },
        'bike': {
            label: 'Bicycle',
            unit: 'km',
            zeroEmission: true
        },
        'walk': {
            label: 'Walk',
            unit: 'km',
            zeroEmission: true
        },
        'shuttle': {
            label: 'Company Shuttle',
            unit: 'km',
            payload: {
                emission_factor: {
                    activity_id: "passenger_vehicle-vehicle_type_bus-fuel_type_na-vehicle_size_na-engine_size_na",
                    data_version: "^0"
                },
                parameters: {
                    distance: 100,
                    distance_unit: "km"
                }
            }
        },
        'telework': {
            label: 'Telework',
            unit: 'km',
            zeroEmission: true
        }
    };

    const countryCodes = ['US', 'CL', 'PE', 'AR', 'BR', 'MX', 'CA', 'EU', 'Other'];

    // Use EXACT payload formats from docs
    const getEmissionFactor = (category, details = {}) => {
        let payload;

        if (category === 'flight') {
            payload = {
                emission_factor: {
                    activity_id: "passenger_flight-route_type_domestic-aircraft_type_na-distance_na-class_na-rf_na-distance_uplift_na",
                    data_version: "^0"
                },
                parameters: {
                    passengers: 4,
                    distance: 100,
                    distance_unit: "km"
                }
            };
        } else if (category === 'hotel') {
            payload = {
                emission_factor: {
                    activity_id: "hospitality_type_hotel",
                    data_version: "^0"
                },
                parameters: {
                    activity: 1,
                    activity_unit: "room_night"
                }
            };
        } else if (category === 'taxi') {
            payload = {
                emission_factor: {
                    activity_id: "passenger_vehicle-vehicle_type_taxi-fuel_type_na-vehicle_size_na-engine_size_na",
                    data_version: "^0"
                },
                parameters: {
                    distance: 100,
                    distance_unit: "km"
                }
            };
        } else if (category === 'rental') {
            payload = {
                emission_factor: {
                    activity_id: "passenger_vehicle-vehicle_type_car-fuel_type_gasoline-vehicle_size_na-engine_size_na",
                    data_version: "^0"
                },
                parameters: {
                    distance: 100,
                    distance_unit: "km"
                }
            };
        } else if (category === 'bus') {
            payload = {
                emission_factor: {
                    activity_id: "passenger_vehicle-vehicle_type_bus-fuel_type_na-vehicle_size_na-engine_size_na",
                    data_version: "^0"
                },
                parameters: {
                    distance: 100,
                    distance_unit: "km"
                }
            };
        } else if (category === 'rail') {
            payload = {
                emission_factor: {
                    activity_id: "passenger_train-route_type_na-fuel_type_na",
                    data_version: "^0"
                },
                parameters: {
                    distance: 100,
                    distance_unit: "km"
                }
            };
        } else if (category === 'car_commute') {
            payload = {
                emission_factor: {
                    activity_id: "passenger_vehicle-vehicle_type_car-fuel_type_gasoline-vehicle_size_na-engine_size_na",
                    data_version: "^0"
                },
                parameters: {
                    distance: 100,
                    distance_unit: "km"
                }
            };
        } else if (category === 'motorcycle') {
            payload = {
                emission_factor: {
                    activity_id: "passenger_vehicle-vehicle_type_motorcycle-fuel_type_na-vehicle_size_na-engine_size_na",
                    data_version: "^0"
                },
                parameters: {
                    distance: 100,
                    distance_unit: "km"
                }
            };
        }

        if (payload) {
            const factor = {
                ...payload.emission_factor,
                name: `${category} Emission Factor`,
                unit: payload.parameters.distance_unit || payload.parameters.activity_unit || 'unit',
                unit_type: payload.parameters.distance ? 'Distance' : 'Activity'
            };

            const factorKey = `${category}_${details.fuelType || ''}_${details.cabinClass || ''}`;
            updateState({
                availableFactors: {
                    ...state.availableFactors,
                    [factorKey]: factor
                }
            });

            return factor;
        }

        return null;
    };

    const handleLoadFactors = () => {
        if (!CLIMATIQ_API_KEY || CLIMATIQ_API_KEY === 'Y2ZBNWZKRzRGNzJYNjRQMzpa') {
            toastr.error('Please configure your Climatiq API key in the .env file');
            return;
        }

        if (travelEntries.length === 0 && !commutingData.headcount) {
            toastr.error('Please add travel entries or commuting data first');
            return;
        }

        updateState({ isSearching: true, availableFactors: {} });
        toastr.info('Loading predefined emission factors...');

        try {
            const loadPromises = [];

            travelEntries.forEach(entry => {
                if (entry.type === 'flight') {
                    loadPromises.push(getEmissionFactor('flight', { cabinClass: entry.cabinClass }));
                } else if (entry.type === 'hotel') {
                    loadPromises.push(getEmissionFactor('hotel'));
                } else if (entry.type === 'ground') {
                    if (entry.mode === 'taxi') {
                        loadPromises.push(getEmissionFactor('taxi'));
                    } else if (entry.mode === 'rental') {
                        loadPromises.push(getEmissionFactor('rental', { fuelType: entry.fuelType }));
                    } else if (entry.mode === 'rail') {
                        loadPromises.push(getEmissionFactor('rail'));
                    } else if (entry.mode === 'bus') {
                        loadPromises.push(getEmissionFactor('bus'));
                    }
                }
            });

            if (commutingData.headcount) {
                loadPromises.push(getEmissionFactor('car_commute', { fuelType: commutingData.carFuelType }));
                loadPromises.push(getEmissionFactor('motorcycle'));
                loadPromises.push(getEmissionFactor('bus'));
            }

            const results = loadPromises.filter(factor => factor !== null);

            if (results.length > 0) {
                toastr.success(`Loaded ${results.length} emission factors! Click Calculate to proceed.`);
            } else {
                toastr.warning('No emission factors could be loaded.');
            }
        } catch (error) {
            console.error('Load error:', error);
            toastr.error('Failed to load emission factors.');
        } finally {
            updateState({ isSearching: false });
        }
    };

    const calculateFlightDistance = (origin, destination) => {
        const routes = {
            'JFK-LAX': 3940, 'JFK-SFO': 4150, 'LAX-ORD': 2800, 'DFW-LAX': 1980,
            'ATL-LAX': 3200, 'ORD-LAX': 2800, 'DEN-LAX': 1350, 'SFO-LAX': 550,
            'SCL-LIM': 2453, 'SCL-BOG': 4250
        };
        const route = `${origin}-${destination}`;
        return routes[route] || 1000;
    };

    // API call function with EXACT payload format
    const callClimateIQAPI = async (payload) => {
        try {
            console.log('API Request:', JSON.stringify(payload, null, 2));

            const response = await fetch('https://api.climatiq.io/data/v1/estimate', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${CLIMATIQ_API_KEY}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.text();
                throw new Error(`API request failed with status ${response.status}: ${errorData}`);
            }

            return await response.json();

        } catch (error) {
            console.error('Error calling ClimateIQ API:', error);
            throw error;
        }
    };

    const calculateTravelEmissions = async () => {
        const results = {};

        for (let i = 0; i < travelEntries.length; i++) {
            const entry = travelEntries[i];

            try {
                let requestBody;

                if (entry.type === 'flight') {
                    const distance = calculateFlightDistance(entry.origin, entry.destination);
                    const totalDistance = distance * parseFloat(entry.trips || 1);
                    const passengers = parseFloat(entry.trips || 1);

                    requestBody = {
                        emission_factor: {
                            activity_id: "passenger_flight-route_type_domestic-aircraft_type_na-distance_na-class_na-rf_na-distance_uplift_na",
                            data_version: "^0"
                        },
                        parameters: {
                            passengers: passengers,
                            distance: totalDistance,
                            distance_unit: "km"
                        }
                    };
                } else if (entry.type === 'hotel') {
                    const roomNights = parseFloat(entry.roomNights || 1);

                    requestBody = {
                        emission_factor: {
                            activity_id: "hospitality_type_hotel",
                            data_version: "^0"
                        },
                        parameters: {
                            activity: roomNights,
                            activity_unit: "room_night"
                        }
                    };
                } else if (entry.type === 'ground') {
                    const distance = parseFloat(entry.distance || 0);

                    if (entry.mode === 'taxi') {
                        requestBody = {
                            emission_factor: {
                                activity_id: "passenger_vehicle-vehicle_type_taxi-fuel_type_na-vehicle_size_na-engine_size_na",
                                data_version: "^0"
                            },
                            parameters: {
                                distance: distance,
                                distance_unit: "km"
                            }
                        };
                    } else if (entry.mode === 'rental') {
                        requestBody = {
                            emission_factor: {
                                activity_id: "passenger_vehicle-vehicle_type_car-fuel_type_gasoline-vehicle_size_na-engine_size_na",
                                data_version: "^0"
                            },
                            parameters: {
                                distance: distance,
                                distance_unit: "km"
                            }
                        };
                    } else if (entry.mode === 'rail') {
                        requestBody = {
                            emission_factor: {
                                activity_id: "passenger_train-route_type_na-fuel_type_na",
                                data_version: "^0"
                            },
                            parameters: {
                                distance: distance,
                                distance_unit: "km"
                            }
                        };
                    } else if (entry.mode === 'bus') {
                        requestBody = {
                            emission_factor: {
                                activity_id: "passenger_vehicle-vehicle_type_bus-fuel_type_na-vehicle_size_na-engine_size_na",
                                data_version: "^0"
                            },
                            parameters: {
                                distance: distance,
                                distance_unit: "km"
                            }
                        };
                    }
                }

                if (requestBody) {
                    const apiResult = await callClimateIQAPI(requestBody);
                    results[`travel_${i}`] = {
                        type: 'travel',
                        description: getTravelDescription(entry),
                        co2e: apiResult.co2e,
                        co2e_unit: apiResult.co2e_unit,
                        calculation_method: 'activity_based_api',
                        api_request: requestBody
                    };
                }
            } catch (error) {
                console.error(`Error calculating emissions for travel entry ${i}:`, error);
                toastr.error(`Calculation failed for ${getTravelDescription(entry)}`);
            }
        }

        return results;
    };

    const calculateCommutingEmissions = async () => {
        const results = {};

        if (!commutingData.headcount || !commutingData.daysPerWeek || !commutingData.weeksPerYear) {
            return results;
        }

        const headcount = parseFloat(commutingData.headcount);
        const daysPerWeek = parseFloat(commutingData.daysPerWeek);
        const weeksPerYear = parseFloat(commutingData.weeksPerYear);

        for (const modeData of commutingData.modes) {
            if (!modeData.percentage || parseFloat(modeData.percentage) === 0) continue;

            const percentage = parseFloat(modeData.percentage) / 100;
            const modeHeadcount = headcount * percentage;

            if (commutingModes[modeData.mode].zeroEmission) {
                results[`commute_${modeData.mode}`] = {
                    type: 'commuting',
                    mode: modeData.mode,
                    description: `${commutingModes[modeData.mode].label} - ${modeHeadcount.toFixed(0)} employees`,
                    co2e: 0,
                    co2e_unit: 'kg',
                    calculation_method: 'zero_emission'
                };
                continue;
            }

            if (!modeData.distance || parseFloat(modeData.distance) === 0) continue;

            const distance = parseFloat(modeData.distance);
            const totalDistance = modeHeadcount * distance * daysPerWeek * weeksPerYear;

            try {
                let requestBody;

                if (modeData.mode === 'car_solo') {
                    requestBody = {
                        emission_factor: {
                            activity_id: "passenger_vehicle-vehicle_type_car-fuel_type_gasoline-vehicle_size_na-engine_size_na",
                            data_version: "^0"
                        },
                        parameters: {
                            distance: totalDistance,
                            distance_unit: "km"
                        }
                    };
                } else if (modeData.mode === 'carpool') {
                    const effectiveDistance = totalDistance / parseFloat(modeData.occupancy || 2.2);
                    requestBody = {
                        emission_factor: {
                            activity_id: "passenger_vehicle-vehicle_type_car-fuel_type_gasoline-vehicle_size_na-engine_size_na",
                            data_version: "^0"
                        },
                        parameters: {
                            distance: effectiveDistance,
                            distance_unit: "km"
                        }
                    };
                } else if (modeData.mode === 'bus' || modeData.mode === 'shuttle') {
                    requestBody = {
                        emission_factor: {
                            activity_id: "passenger_vehicle-vehicle_type_bus-fuel_type_na-vehicle_size_na-engine_size_na",
                            data_version: "^0"
                        },
                        parameters: {
                            distance: totalDistance,
                            distance_unit: "km"
                        }
                    };
                } else if (modeData.mode === 'metro') {
                    requestBody = {
                        emission_factor: {
                            activity_id: "passenger_train-route_type_urban_rail-fuel_type_na",
                            data_version: "^0"
                        },
                        parameters: {
                            distance: totalDistance,
                            distance_unit: "km"
                        }
                    };
                } else if (modeData.mode === 'rail') {
                    requestBody = {
                        emission_factor: {
                            activity_id: "passenger_train-route_type_regional_rail-fuel_type_na",
                            data_version: "^0"
                        },
                        parameters: {
                            distance: totalDistance,
                            distance_unit: "km"
                        }
                    };
                } else if (modeData.mode === 'motorcycle') {
                    requestBody = {
                        emission_factor: {
                            activity_id: "passenger_vehicle-vehicle_type_motorcycle-fuel_type_na-vehicle_size_na-engine_size_na",
                            data_version: "^0"
                        },
                        parameters: {
                            distance: totalDistance,
                            distance_unit: "km"
                        }
                    };
                }

                if (requestBody) {
                    const apiResult = await callClimateIQAPI(requestBody);
                    results[`commute_${modeData.mode}`] = {
                        type: 'commuting',
                        mode: modeData.mode,
                        description: `${commutingModes[modeData.mode].label} - ${modeHeadcount.toFixed(0)} employees`,
                        co2e: apiResult.co2e,
                        co2e_unit: apiResult.co2e_unit,
                        calculation_method: 'activity_based_api',
                        passenger_km: totalDistance,
                        api_request: requestBody
                    };
                }
            } catch (error) {
                console.error(`Error calculating emissions for commuting mode ${modeData.mode}:`, error);
            }
        }

        return results;
    };

    const getTravelDescription = (entry) => {
        switch (entry.type) {
            case 'flight':
                return `${entry.origin} → ${entry.destination} (${entry.cabinClass}) - ${entry.trips} trips`;
            case 'hotel':
                return `${entry.city}, ${entry.country} - ${entry.roomNights} room-nights`;
            case 'ground':
                return `${entry.mode} - ${entry.distance} km${entry.fuelType && entry.fuelType !== 'unknown' ? ` (${entry.fuelType})` : ''}`;
            default:
                return 'Unknown travel type';
        }
    };

    const handleCalculate = async () => {
        if (!CLIMATIQ_API_KEY || CLIMATIQ_API_KEY === 'Y2ZBNWZKRzRGNzJYNjRQMzpa') {
            toastr.error('Please configure your Climatiq API key in the .env file');
            return;
        }

        if (travelEntries.length === 0 && !commutingData.headcount) {
            toastr.error('Please add travel entries or commuting data first');
            return;
        }

        updateState({ isCalculating: true, calculationResults: {} });
        toastr.info('Calculating emissions via API...');

        try {
            const travelResults = await calculateTravelEmissions();
            const commuteResults = await calculateCommutingEmissions();

            const allResults = { ...travelResults, ...commuteResults };
            updateState({ calculationResults: allResults });

            const totalEmissions = Object.values(allResults).reduce((sum, result) => sum + (result.co2e || 0), 0);

            if (Object.keys(allResults).length > 0) {
                toastr.success(`Calculation complete! Total: ${totalEmissions.toFixed(2)} kg CO₂e`);
            } else {
                toastr.warning('No emissions could be calculated. Please check your inputs.');
            }
        } catch (error) {
            toastr.error('Calculation failed. Please check your inputs and try again.');
            console.error('Calculation error:', error);
        } finally {
            updateState({ isCalculating: false });
        }
    };

    const handleAddTravelEntry = () => {
        const newEntries = [...travelEntries, {
            type: '', origin: '', destination: '', cabinClass: '', trips: '',
            roomNights: '', city: '', country: 'US',
            distance: '', mode: '', fuelType: 'unknown'
        }];
        updateState({ travelEntries: newEntries });
    };

    const handleTravelEntryChange = (index, field, value) => {
        const updated = [...travelEntries];
        updated[index][field] = value;
        updateState({ travelEntries: updated });
    };

    const handleRemoveTravelEntry = (index) => {
        updateState({ travelEntries: travelEntries.filter((_, i) => i !== index) });
    };

    const handleCommutingChange = (field, value) => {
        updateState({ commutingData: { ...commutingData, [field]: value } });
    };

    const handleCommutingModeChange = (index, field, value) => {
        const updatedModes = [...commutingData.modes];
        updatedModes[index][field] = value;
        updateState({ commutingData: { ...commutingData, modes: updatedModes } });
    };

    const loadSampleData = () => {
        updateState({
            formData: { ...formData, year: '2025' },
            travelEntries: [
                {
                    type: 'flight', origin: 'JFK', destination: 'LAX',
                    cabinClass: 'economy', trips: '180',
                    roomNights: '', city: '', country: 'US',
                    distance: '', mode: '', fuelType: 'unknown'
                },
                {
                    type: 'hotel', origin: '', destination: '', cabinClass: '',
                    trips: '', roomNights: '720', city: 'New York', country: 'US',
                    distance: '', mode: '', fuelType: 'unknown'
                }
            ],
            commutingData: {
                headcount: '500', daysPerWeek: '3.5', weeksPerYear: '48',
                modes: [
                    { mode: 'car_solo', percentage: '45', distance: '24' },
                    { mode: 'carpool', percentage: '15', distance: '24', occupancy: '2.2' },
                    { mode: 'bus', percentage: '10', distance: '30' },
                    { mode: 'metro', percentage: '20', distance: '28' },
                    { mode: 'rail', percentage: '', distance: '' },
                    { mode: 'motorcycle', percentage: '', distance: '' },
                    { mode: 'bike', percentage: '5', distance: '5' },
                    { mode: 'walk', percentage: '', distance: '' },
                    { mode: 'shuttle', percentage: '', distance: '' },
                    { mode: 'telework', percentage: '5', distance: '0' }
                ],
                carFuelType: 'gasoline'
            }
        });
        toastr.info('Sample data loaded. Click "Load Factors" to get emission factors.');
    };

    const totalEmissions = Object.values(state.calculationResults || {}).reduce((sum, result) => sum + (result.co2e || 0), 0);
    const travelEmissions = Object.values(state.calculationResults || {}).filter(r => r.type === 'travel').reduce((sum, r) => sum + (r.co2e || 0), 0);
    const commuteEmissions = Object.values(state.calculationResults || {}).filter(r => r.type === 'commuting').reduce((sum, r) => sum + (r.co2e || 0), 0);

    return (
        <div className="max-w-6xl mx-auto px-4 py-8 pb-24">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="p-8">
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-yellow-800">
                            <strong>API Status:</strong> {CLIMATIQ_API_KEY && CLIMATIQ_API_KEY !== 'Y2ZBNWZKRzRGNzJYNjRQMzpa' ?
                                '✅ API Key Configured' : '❌ Please set REACT_APP_CLIMATIQ_API_KEY in .env file'}
                        </p>
                        <p className="text-xs text-yellow-700 mt-1">
                            <strong>Note:</strong> Using EXACT Climatiq payload formats as provided.
                        </p>
                    </div>

                    <div className="flex justify-end mb-6">
                        <button
                            onClick={loadSampleData}
                            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all duration-200 text-sm"
                        >
                            Load Sample Data
                        </button>
                    </div>

                    {(travelEntries.length > 0 || commutingData.headcount || Object.keys(state.calculationResults || {}).length > 0) && (
                        <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                <h3 className="text-sm font-semibold text-blue-800">Travel Entries</h3>
                                <p className="text-2xl font-bold text-blue-600">{travelEntries.length}</p>
                            </div>
                            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                                <h3 className="text-sm font-semibold text-green-800">Total Emissions</h3>
                                <p className="text-2xl font-bold text-green-600">{totalEmissions.toFixed(0)} kg CO₂e</p>
                            </div>
                            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                                <h3 className="text-sm font-semibold text-purple-800">Business Travel</h3>
                                <p className="text-2xl font-bold text-purple-600">{travelEmissions.toFixed(0)} kg</p>
                            </div>
                            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                                <h3 className="text-sm font-semibold text-orange-800">Commuting</h3>
                                <p className="text-2xl font-bold text-orange-600">{commuteEmissions.toFixed(0)} kg</p>
                            </div>
                        </div>
                    )}

                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Reporting Year *</label>
                        <select
                            value={formData.year || new Date().getFullYear().toString()}
                            onChange={(e) => updateState({ formData: { ...formData, year: e.target.value } })}
                            className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            {Array.from({ length: new Date().getFullYear() - 2009 }, (_, i) =>
                                (new Date().getFullYear() - i).toString()
                            ).map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                    </div>

                    <div className="mb-6">
                        <button
                            onClick={handleLoadFactors}
                            disabled={state.isSearching}
                            className="px-6 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {state.isSearching ? (
                                <FaSpinner className="w-4 h-4 animate-spin" />
                            ) : (
                                <FaDownload className="w-4 h-4" />
                            )}
                            <span>{state.isSearching ? 'Loading...' : 'Load Emission Factors'}</span>
                        </button>
                        <p className="text-xs text-gray-500 mt-2">
                            Load predefined Climatiq emission factors with exact payload formats.
                        </p>
                    </div>

                    {Object.keys(state.availableFactors || {}).length > 0 && (
                        <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                            <h3 className="font-semibold text-green-800 mb-2">Available Emission Factors</h3>
                            <div className="space-y-2">
                                {Object.entries(state.availableFactors).map(([key, factor]) => (
                                    <div key={key} className="text-sm text-green-700">
                                        <strong>{key}:</strong> {factor.activity_id}
                                        <br />
                                        <span className="text-xs">Data Version: {factor.data_version}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="mb-8">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-semibold text-gray-800 flex items-center space-x-2">
                                <FaPlane className="w-5 h-5 text-blue-500" />
                                <span>Business Travel</span>
                            </h2>
                            <button
                                onClick={handleAddTravelEntry}
                                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all duration-200 flex items-center space-x-2 text-sm"
                            >
                                <FaPlus className="w-4 h-4" />
                                <span>Add Travel Entry</span>
                            </button>
                        </div>

                        {travelEntries.length === 0 ? (
                            <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                                <FaPlane className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-500">No travel entries added yet</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {travelEntries.map((entry, index) => (
                                    <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                        <div className="flex items-center justify-between mb-3">
                                            <h3 className="font-semibold text-gray-700">
                                                {travelTypes[entry.type]?.label || 'Travel Entry'} #{index + 1}
                                            </h3>
                                            <button
                                                onClick={() => handleRemoveTravelEntry(index)}
                                                className="text-red-500 hover:text-red-700"
                                            >
                                                <FaTrash className="w-4 h-4" />
                                            </button>
                                        </div>

                                        {travelTypes[entry.type]?.payload && (
                                            <div className="mb-3 p-3 bg-blue-100 rounded-lg border border-blue-200">
                                                <p className="text-xs text-blue-800">
                                                    <strong>Payload Type:</strong> {travelTypes[entry.type].payload.parameters.passengers ? 'Flight' :
                                                        travelTypes[entry.type].payload.parameters.activity ? 'Hotel' : 'Ground Transport'}
                                                    <br />
                                                    <strong>Activity ID:</strong> {travelTypes[entry.type].payload.emission_factor.activity_id}
                                                </p>
                                            </div>
                                        )}

                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                                                <select
                                                    value={entry.type}
                                                    onChange={(e) => handleTravelEntryChange(index, 'type', e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                >
                                                    <option value="">Select Type</option>
                                                    <option value="flight">Flight</option>
                                                    <option value="hotel">Hotel Stay</option>
                                                    <option value="ground">Ground Transport</option>
                                                </select>
                                            </div>

                                            {entry.type === 'flight' && (
                                                <>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">Origin Airport *</label>
                                                        <input
                                                            type="text"
                                                            value={entry.origin}
                                                            onChange={(e) => handleTravelEntryChange(index, 'origin', e.target.value)}
                                                            placeholder="e.g., JFK"
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">Destination Airport *</label>
                                                        <input
                                                            type="text"
                                                            value={entry.destination}
                                                            onChange={(e) => handleTravelEntryChange(index, 'destination', e.target.value)}
                                                            placeholder="e.g., LAX"
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">Cabin Class *</label>
                                                        <select
                                                            value={entry.cabinClass}
                                                            onChange={(e) => handleTravelEntryChange(index, 'cabinClass', e.target.value)}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                        >
                                                            <option value="">Select Class</option>
                                                            <option value="economy">Economy</option>
                                                            <option value="premium-economy">Premium Economy</option>
                                                            <option value="business">Business</option>
                                                            <option value="first">First Class</option>
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">Number of Trips *</label>
                                                        <input
                                                            type="number"
                                                            value={entry.trips}
                                                            onChange={(e) => handleTravelEntryChange(index, 'trips', e.target.value)}
                                                            placeholder="e.g., 10"
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                        />
                                                    </div>
                                                </>
                                            )}

                                            {entry.type === 'hotel' && (
                                                <>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                                                        <input
                                                            type="text"
                                                            value={entry.city}
                                                            onChange={(e) => handleTravelEntryChange(index, 'city', e.target.value)}
                                                            placeholder="e.g., New York"
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">Country *</label>
                                                        <select
                                                            value={entry.country}
                                                            onChange={(e) => handleTravelEntryChange(index, 'country', e.target.value)}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                        >
                                                            {countryCodes.map(code => (
                                                                <option key={code} value={code}>{code}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">Room Nights *</label>
                                                        <input
                                                            type="number"
                                                            value={entry.roomNights}
                                                            onChange={(e) => handleTravelEntryChange(index, 'roomNights', e.target.value)}
                                                            placeholder="e.g., 30"
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                        />
                                                    </div>
                                                </>
                                            )}

                                            {entry.type === 'ground' && (
                                                <>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">Mode *</label>
                                                        <select
                                                            value={entry.mode}
                                                            onChange={(e) => handleTravelEntryChange(index, 'mode', e.target.value)}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                        >
                                                            <option value="">Select Mode</option>
                                                            <option value="taxi">Taxi</option>
                                                            <option value="rental">Rental Car</option>
                                                            <option value="rail">Rail</option>
                                                            <option value="bus">Bus</option>
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">Distance (km) *</label>
                                                        <input
                                                            type="number"
                                                            value={entry.distance}
                                                            onChange={(e) => handleTravelEntryChange(index, 'distance', e.target.value)}
                                                            placeholder="e.g., 50"
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                        />
                                                    </div>
                                                    {entry.mode === 'rental' && (
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">Fuel Type</label>
                                                            <select
                                                                value={entry.fuelType}
                                                                onChange={(e) => handleTravelEntryChange(index, 'fuelType', e.target.value)}
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                            >
                                                                <option value="unknown">Unknown</option>
                                                                <option value="gasoline">Gasoline</option>
                                                                <option value="diesel">Diesel</option>
                                                                <option value="hybrid">Hybrid</option>
                                                                <option value="ev">Electric</option>
                                                            </select>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="mb-8">
                        <h2 className="text-xl font-semibold text-gray-800 flex items-center space-x-2 mb-4">
                            <FaUserFriends className="w-5 h-5 text-green-500" />
                            <span>Employee Commuting</span>
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Headcount *</label>
                                <input
                                    type="number"
                                    value={commutingData.headcount}
                                    onChange={(e) => handleCommutingChange('headcount', e.target.value)}
                                    placeholder="e.g., 500"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Days Per Week *</label>
                                <input
                                    type="number"
                                    step="0.1"
                                    value={commutingData.daysPerWeek}
                                    onChange={(e) => handleCommutingChange('daysPerWeek', e.target.value)}
                                    placeholder="e.g., 3.5"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Weeks Per Year *</label>
                                <input
                                    type="number"
                                    value={commutingData.weeksPerYear}
                                    onChange={(e) => handleCommutingChange('weeksPerYear', e.target.value)}
                                    placeholder="e.g., 48"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                />
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Car Fuel Type (for car modes)</label>
                            <select
                                value={commutingData.carFuelType}
                                onChange={(e) => handleCommutingChange('carFuelType', e.target.value)}
                                className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg"
                            >
                                <option value="unknown">Unknown</option>
                                <option value="gasoline">Gasoline</option>
                                <option value="diesel">Diesel</option>
                                <option value="hybrid">Hybrid</option>
                                <option value="ev">Electric</option>
                            </select>
                        </div>

                        <div className="space-y-4">
                            {commutingData.modes.map((modeData, index) => (
                                <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                    {commutingModes[modeData.mode]?.payload && (
                                        <div className="mb-3 p-2 bg-blue-100 rounded border border-blue-200">
                                            <p className="text-xs text-blue-800">
                                                <strong>Activity ID:</strong> {commutingModes[modeData.mode].payload.emission_factor.activity_id}
                                            </p>
                                        </div>
                                    )}
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                {commutingModes[modeData.mode].label}
                                            </label>
                                            <input
                                                type="number"
                                                value={modeData.percentage}
                                                onChange={(e) => handleCommutingModeChange(index, 'percentage', e.target.value)}
                                                placeholder="% of employees"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Distance (km)</label>
                                            <input
                                                type="number"
                                                value={modeData.distance}
                                                onChange={(e) => handleCommutingModeChange(index, 'distance', e.target.value)}
                                                placeholder="One-way distance"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                            />
                                        </div>
                                        {modeData.mode === 'carpool' && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Occupancy</label>
                                                <input
                                                    type="number"
                                                    step="0.1"
                                                    value={modeData.occupancy}
                                                    onChange={(e) => handleCommutingModeChange(index, 'occupancy', e.target.value)}
                                                    placeholder="e.g., 2.2"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {Object.keys(state.calculationResults || {}).length > 0 && (
                        <div className="mt-8 p-6 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Calculation Results Summary</h3>
                            <div className="space-y-3">
                                {Object.entries(state.calculationResults).map(([key, result]) => (
                                    <div key={key} className="flex justify-between items-center p-3 bg-white rounded-lg border border-gray-200">
                                        <div>
                                            <span className="font-medium text-gray-700">
                                                {result.description}
                                            </span>
                                            <p className="text-sm text-gray-500">
                                                {result.type === 'travel' ? 'Business Travel' : 'Employee Commuting'}
                                            </p>
                                        </div>
                                        <span className="font-semibold text-green-700">
                                            {result.co2e.toFixed(2)} {result.co2e_unit}
                                        </span>
                                    </div>
                                ))}
                                <div className="border-t border-gray-300 pt-3 mt-2">
                                    <div className="flex justify-between items-center font-bold text-lg">
                                        <span>Total Business Travel & Commuting Emissions:</span>
                                        <span className="text-blue-700">
                                            {totalEmissions.toFixed(2)} kg CO₂e
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="mt-6 flex justify-end space-x-3">
                        <button
                            onClick={handleCalculate}
                            disabled={state.isCalculating}
                            className="px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {state.isCalculating ? (
                                <FaSpinner className="w-4 h-4 animate-spin" />
                            ) : (
                                <FaCalculator className="w-4 h-4" />
                            )}
                            <span>{state.isCalculating ? 'Calculating...' : 'Calculate via API'}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Api11Form;